/**
 * hazards.ts — the three rules that guard a value an instrument already prints.
 *
 * A HAZARD IS NOT A VALIDATION. Every other rule on this canvas answers "is this value well
 * formed"; these answer "who has already read this value, and what happens to their copy if it
 * moves". The vehicle, the money and the overdue interest rate are the three places where the
 * answer is not "nothing": all are printed onto instruments that get frozen at the first signature
 * and are then never re-rendered, so a silent change leaves a signed document describing a car
 * that was swapped, quoting a price nobody agreed to, or quoting a rate the overdue engine no
 * longer charges.
 *
 * ── What each value actually prints on (verified against the seeded templates) ──────────────────
 *
 * - THE VEHICLE IS NAMED BY THE HIRE PURCHASE AGREEMENT ALONE — and by more of it than before.
 *   The restructured schedule identifies the car by `vehicle.model`, `vehicle.year_of_manufacture`,
 *   `vehicle.colour` and `vehicle.registration`: a plate is what a recovery agent or an insurer
 *   matches on, and the year and colour are what they match it against. (`vehicle.mileage` is NOT
 *   among them — the schedule's Vehicle particulars stop at the condition — so the odometer is an
 *   operational record, cleared with the assignment for that reason rather than this one.) The witness
 *   and satellite schedules name the car only to identify the hire purchase agreement they hang off;
 *   the deposit agreement names the driver and the money and no car. So a vehicle change still has
 *   exactly one instrument to answer for, however many are live.
 * - THE MONEY IS THE HIRE PURCHASE AGREEMENT'S TOO. `contract.selling_price` and
 *   `contract.weekly_instalment` are the Hire-purchase price and the Weekly Instalment of the
 *   schedule's money particulars, and they appear on no other instrument: the advance schedules
 *   carry `loan.*` / `insurance_loan.*` figures of their own and the deposit agreement carries
 *   `deposit.amount`. Neither of those is edited on the Financials canvas. The schedule's Term and
 *   Expected completion are DERIVED from these two and the deposit, so a money change moves them
 *   as well — one more reason a live document has to be answered for rather than re-rendered.
 * - THE OVERDUE RATE PRINTS ON THREE, AS A DAILY FIGURE. Clause 2.3 states it on all three — the
 *   hire purchase schedule, the operational expense advance and the insurance premium advance — and
 *   each states it again in its own particulars. The key they carry is `contract.overdue_daily_rate`
 *   — `contracts.overdue_interest_rate / 365`, printed to four places — so the stored column this
 *   rule guards is the same one, reached through a derivation. The deposit agreement carries none,
 *   and neither does the insurance premium PAYMENT RECORD [D-B45]: nothing is advanced under it, so
 *   there is no clause 2.3 and no rate row for the column to contradict. It shares the ADVANCE'S
 *   `doc_type`, which is why the rate rule asks `printsTheOverdueRate` rather than the type alone.
 *
 * ── What "frozen" means, and why the gate is drawn there ────────────────────────────────────────
 *
 * `agreement_render` renders LIVE from current merge data until `content_sha256` is written, and
 * returns the frozen markdown verbatim afterwards (`20260801010000:3114-3116, 3175-3182`). The
 * freeze happens inside `agreement_freeze_if_needed`, which is called when a signing link is issued
 * or a countersignature is taken — both of which move the document out of `PENDING_REVIEW`. So
 * "past PENDING_REVIEW" and "its text can no longer follow the contract" are the same statement,
 * and that is where the read-only gate sits. A paper original (`origin = SCANNED`, a legacy
 * `signed_at` with no in-app document) is the strongest form of frozen and the one thing on this
 * page that cannot be voided at all.
 *
 * ── Client-side only, and stated as such ────────────────────────────────────────────────────────
 *
 * The database guards a change of DRIVER or WITNESS: `contracts_void_agreements_on_party_change`
 * voids the live documents in scope on a DRAFT contract and RAISES past DRAFT while any of them
 * exists (`20260805020000:1766-1878`). WHICH DOCUMENTS ARE IN SCOPE DEPENDS ON WHO MOVED [D-B18]:
 * a driver move keeps the broad reach — every live document on the contract, because every one is
 * built for a contract they are a party to — while a witness move reaches only the documents that
 * SEAT a witness, read off the roster rather than off `doc_type`, so a v2 three-party hire purchase
 * document is still covered and a v3 two-party one is not. Both doors carry the same predicate.
 *
 * Its trigger is `AFTER UPDATE OF driver_id, witness_id`, so the CONTRACT'S OTHER INSTRUMENTS are
 * this module's to guard when the vehicle moves: nothing in SQL voids a hire purchase agreement
 * over a change of car.
 *
 * ONE COLUMN OF THIS RULE IS NOW WATCHED, AND ONLY FOR ONE INSTRUMENT [D-B48].
 * `trg_contracts_void_partner_schedule` is `AFTER UPDATE OF cash_price, management_rate, sold_at,
 * vehicle_id` (the cash price joined at [D-B59]) and is scoped entirely to the Vehicle Schedule:
 * on a DRAFT contract it DELETES the live `PARTNER_MANAGEMENT` document and the unsealed referent
 * with the cash price copied onto it, and on a sealed one it refuses by name with
 * `PARTNER_SCHEDULE_RECORD_IMMUTABLE`. Past DRAFT it does nothing at all. So the schedule is the
 * server's business here and the other instruments are still this module's — which is why
 * `VEHICLE_INSTRUMENTS` and `MONEY_INSTRUMENTS` name the hire purchase agreement alone: everything
 * in those lists is voided BY THIS CLIENT, and a schedule added to them would be voided twice, the
 * second time through a door (`agreement_void`) that deliberately KEEPS the referent the server's
 * trigger deletes.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only, and a gating table is the kind of
 * rule that has to be pinned by a test rather than by a screenshot. `BoardSlot` is imported as a
 * TYPE, so nothing here pulls a component graph into the test run; `instrument.ts` is a value
 * import and is safe for the same reason stated the other way round — it has no imports of its
 * own at all, which is why the instrument vocabulary lives there rather than beside the icons in
 * `constants.ts`.
 */

import type { BoardSlot } from '@/components/agreements/boardSlots';
import {
    INSTRUMENT_NOUNS as INSURANCE_INSTRUMENT_NOUNS,
    type InsuranceInstrument,
} from '@/lib/agreements/instrument';

// ─── The instruments, as this rule needs to see them ─────────────────────────

export type HazardDocType =
    | 'HIRE_PURCHASE'
    | 'WITNESS'
    | 'LOAN'
    | 'INSURANCE_LOAN'
    | 'DEPOSIT'
    | 'PARTNER_MANAGEMENT';

/** The three live document statuses. `VOIDED` and `SUPERSEDED` never reach a slot. */
export type LiveDocStatus = 'PENDING_REVIEW' | 'IN_SIGNING' | 'COMPLETED';

/**
 * One instrument, flattened out of its board slot.
 *
 * A SLOT IS A DOCUMENT THAT COULD EXIST, so all three of these are real and distinct: no document
 * and no signature (nothing to answer for), a live document (voidable here), and a legacy
 * `signed_at` with no document at all (a paper original — signed, binding, and beyond this page).
 */
export interface InstrumentFact {
    /** `slotKey(docType, subjectId)`. */
    key: string;
    docType: HazardDocType;
    /**
     * WHICH INSURANCE INSTRUMENT THIS IS [D-B45] — set on `INSURANCE_LOAN` and null everywhere
     * else, carried straight off the slot rather than re-derived.
     *
     * `doc_type` stopped being enough to name this row, or to say what it prints. The Insurance
     * Premium Payment Record and the Insurance Premium Advance share a type, and every sentence in
     * this file names its documents out loud — so a noun read from the type alone would tell an
     * admin they are about to void an advance that does not exist.
     */
    instrument: InsuranceInstrument | null;
    /** What `agreement_create` is called with when the document is reissued. */
    subjectId: number;
    /** The document's own title, for a dialog that has to name it. */
    title: string;
    /** The live document's status; null when the arc carries no in-app document. */
    status: LiveDocStatus | null;
    documentId: number | null;
    referenceNo: string | null;
    /** The legacy row's signature date — set by a paper original too. */
    legacySignedAt: string | null;
    signedCount: number;
    partyCount: number;
}

/**
 * How each instrument is spoken about in a sentence. Not the `DOC_TYPES` label, deliberately.
 *
 * The two advances are named as their published schedules are titled — "Insurance Premium Advance"
 * and "Operational Expense Advance" — lower-cased for mid-sentence use. An admin reading a hazard
 * confirm has the document open beside it, and a noun that named it something else would read as a
 * second instrument rather than as the one about to be voided.
 *
 * `INSURANCE_LOAN`'S ENTRY IS THE ADVANCE'S, AND IT IS NOW A FALLBACK [D-B45]. That doc type
 * carries two published instruments, so nothing in this file reads this map directly any more:
 * `instrumentNoun` does, after asking the fact which of the two it is. The entry stays worded as
 * the advance because that is what an unknown shape falls back to.
 */
export const INSTRUMENT_NOUNS: Record<HazardDocType, string> = {
    HIRE_PURCHASE: 'hire purchase agreement',
    WITNESS: 'witness agreement',
    LOAN: 'operational expense advance',
    INSURANCE_LOAN: 'insurance premium advance',
    DEPOSIT: 'deposit agreement',
    // 'VEHICLE SCHEDULE' AND NOT THE PUBLISHED TITLE [D-B48]. `agreement_templates.title` on
    // `partner_vehicle_schedule` v1 is "Hire Fleet Management Agreement", which is what the receipt
    // prints and what an admin searches the PDF store for — but these are MID-SENTENCE nouns, and
    // "this voids the hire fleet management agreement" names the Terms booklet the schedule cites
    // rather than the signed page about to be withdrawn. The booklet is unsigned, versioned and
    // nobody's to void; the schedule is the instrument, and the whole page calls it one.
    PARTNER_MANAGEMENT: 'vehicle schedule',
};

/**
 * The noun for ONE instrument, which on the insurance arc depends on WHICH instrument it is.
 *
 * EVERY SENTENCE IN THIS FILE GOES THROUGH IT, so there is one place the two names are chosen
 * between rather than seven. A hazard confirm that said "this voids the insurance premium advance"
 * over an Insurance Premium Payment Record would be naming a document that is not on the board —
 * and it is the sentence an admin reads immediately before destroying something.
 *
 * By type where the instrument is unknown, which is the advance for `INSURANCE_LOAN` — a fact
 * assembled without the slot's instrument is a fact about the shape this app has always drawn.
 */
function nounOfType(docType: HazardDocType, instrument: InsuranceInstrument | null): string {
    if (docType === 'INSURANCE_LOAN' && instrument !== null) {
        return INSURANCE_INSTRUMENT_NOUNS[instrument];
    }
    return INSTRUMENT_NOUNS[docType];
}

/** The same answer, for a fact this file has already built. */
export function instrumentNoun(instrument: InstrumentFact): string {
    return nounOfType(instrument.docType, instrument.instrument);
}

/** Templates whose required fields name the car. Verified: the hire purchase agreement alone. */
export const VEHICLE_INSTRUMENTS: readonly HazardDocType[] = ['HIRE_PURCHASE'];

/**
 * Templates that print the selling price and the weekly instalment — the schedule's money particulars.
 *
 * THE HIRE PURCHASE AGREEMENT ALONE, again, and for a reason worth stating: the advance schedules
 * also print money, but it is `loan.principal_amount` and `insurance_loan.principal_amount`, which
 * are edited in Insurance and not on the Financials canvas. Nothing this rule guards moves them.
 */
export const MONEY_INSTRUMENTS: readonly HazardDocType[] = ['HIRE_PURCHASE'];

/**
 * Templates that print the contract's overdue rate — as `contract.overdue_daily_rate` now, which is
 * the same stored `contracts.overdue_interest_rate` divided by 365 and printed to four places.
 * The key moved; the column this rule guards did not.
 *
 * 'LOAN' STAYS, AND IT IS STILL DORMANT — unlike `CITING_INSTRUMENTS` below, which the widened
 * cascade genuinely reaches (owner's ruling, 2026-09-16). This list is consulted by
 * `printsTheOverdueRate` and through it by `overdueRateControl`, whose only caller is
 * `FinancialsArea.tsx:227` — instruments built from the SETUP canvas's own board, which mints no
 * LOAN slot [D-B17] and is issued no read for one. The `'all'` board does mint them, but nothing
 * hands that board to this rule, and PR B must not without a ruling: the rate lock exists to stop
 * a figure moving under a frozen contract instrument, and a driver's standing advance is a debt the
 * contract was never written around.
 *
 * IT WAS ALWAYS KEPT FOR THE STATEMENT RATHER THAN FOR THE REACH: clause 2.3 of the operational
 * expense advance carries the rate, production holds 154 sealed documents of that type across 75
 * contracts, and a list that dropped it would read as a claim that they do not quote the rate —
 * they were sealed under the instrument's former title and quote it just the same. The rule this
 * list feeds is dormant in a second way besides: `overdueRateControl` short-circuits on
 * `status !== 'DRAFT'` before it is ever consulted, and no contract in production is DRAFT.
 */
export const RATE_INSTRUMENTS: readonly HazardDocType[] = [
    'HIRE_PURCHASE',
    'LOAN',
    'INSURANCE_LOAN',
];

/**
 * Whether THIS instrument's text quotes the contract's overdue rate — the list above, minus the
 * one member of `INSURANCE_LOAN` that carries no rate at all [D-B45].
 *
 * THE EXCLUSION IS A FACT ABOUT THE BODY, NOT A RELAXATION. The Insurance Premium Payment Record
 * prints no rate of any kind: nothing is advanced under it, no interest arises and nothing is owed,
 * so there is no clause 2.3 and no particulars row for a rate to sit in. Holding
 * `contracts.overdue_interest_rate` read-only behind a sealed Record would lock a column over a
 * document that never mentions it — and on a contract whose only insurance loan is fully deposited
 * the Record is the only insurance instrument it will ever carry, so the lock would be permanent and
 * groundless.
 *
 * `RATE_INSTRUMENTS` KEEPS ITS THIRD MEMBER, because the STATEMENT is about the advance and stays
 * true: `insurance_loan v5`'s clause 2.3 quotes `contract.overdue_daily_rate`, and every one of the
 * sealed advances in production was rendered with it.
 */
export function printsTheOverdueRate(instrument: InstrumentFact): boolean {
    if (instrument.docType === 'INSURANCE_LOAN' && instrument.instrument === 'PAYMENT_RECORD') {
        return false;
    }
    return RATE_INSTRUMENTS.includes(instrument.docType);
}

/**
 * Templates whose frozen prose CITES the hire purchase document by reference, so a reissue of that
 * document leaves them naming an instrument nobody can produce [D-B44].
 *
 * MEASURED AGAINST THE PUBLISHED CATALOGUE, NOT GUESSED. `witness v2` carries
 * `{{contract.hire_purchase_reference}}` twice and `{{contract.hire_purchase_signed_date}}` once;
 * `insurance_loan v5` and `loan v6` carry the reference three times each. `deposit v1` and
 * `hire_purchase v8` carry neither — the deposit agreement answers for the money it took and the
 * hire purchase agreement is the citation's subject rather than one of its holders.
 *
 * 'LOAN' STAYS, AND THE CASCADE IS WHERE IT STOPPED BEING DORMANT (owner's ruling, 2026-09-16).
 * The advance schedule still cites the reference three times — v6 when this list was measured, v7
 * today — and the setup board still mints no LOAN slot [D-B17], but `cascadeSlots` is a board read
 * at full width whatever the caller's scope is, and it now carries
 * the contract's standing advances — so a hire purchase void that leaves a sealed advance behind is
 * a hazard this list actually reports rather than one it merely describes. That is the whole reason
 * the census is fed the advances: a cascade blind to them would say it had carried everything.
 *
 * 'INSURANCE_LOAN' COVERS BOTH ITS INSTRUMENTS, AND HERE THAT IS RIGHT RATHER THAN COARSE [D-B45].
 * The Insurance Premium Payment Record cites no Terms booklet — it is self-contained — but its
 * operative clause on the hire purchase arrangement DOES name `{{contract.hire_purchase_reference}}`
 * ("separate; changes nothing agreed there; nothing is owed under this Record"), so a reissue of
 * the master leaves a sealed Record quoting a reference nobody can produce, exactly as it does an
 * advance. Unlike the rate, this list needs no carve-out: both members of the type belong in it.
 *
 * 'PARTNER_MANAGEMENT' IS NOT A MEMBER, AND THAT IS A READING OF THE TEMPLATE RATHER THAN AN
 * OVERSIGHT [D-B48]. The Vehicle Schedule cites the Primelane Hire Fleet Management Terms booklet
 * by version and sha256 — a citation of an UNSIGNED, versioned instrument, which is not what this
 * list is about — and it names `{{contract.hire_purchase_reference}}` nowhere at all. It could not:
 * the schedule is executed BEFORE the hire purchase agreement exists, because `agreement_sign`
 * refuses the driver's seat until the partner has signed, so at freeze time there is no reference
 * for it to quote. Voiding and reissuing the master therefore leaves a sealed schedule citing
 * nothing that has moved, and a banner naming it would be reporting a hazard that is not one.
 */
export const CITING_INSTRUMENTS: readonly HazardDocType[] = ['WITNESS', 'INSURANCE_LOAN', 'LOAN'];

/**
 * The board's slots, as facts.
 *
 * TYPE-ONLY IMPORT, so this stays testable: `boardSlots.ts` reaches `@/lib/format` and the doc-type
 * catalogue, and a value import would drag both into a node-env test run for the sake of five
 * property reads.
 */
export function instrumentsFromSlots(slots: BoardSlot[]): InstrumentFact[] {
    return slots.map((slot) => {
        const document = slot.document;
        const signatures = document?.signatures ?? [];
        const docType = slot.docType as HazardDocType;
        const instrument = slot.instrument ?? null;
        return {
            key: slot.key,
            docType,
            instrument,
            subjectId: slot.subjectId,
            title: document?.title ?? nounOfType(docType, instrument),
            status: (document?.status as LiveDocStatus | undefined) ?? null,
            documentId: document?.id ?? null,
            referenceNo: document?.reference_no ?? null,
            legacySignedAt: slot.legacySignedAt,
            signedCount: signatures.filter((signature) => signature.status === 'SIGNED').length,
            partyCount: signatures.length,
        };
    });
}

// ─── Predicates ──────────────────────────────────────────────────────────────

/** A live in-app document — the only thing this page can void. */
export function isVoidable(instrument: InstrumentFact): boolean {
    return instrument.documentId !== null && instrument.status !== null;
}

/** A paper original: signed off-system, recorded here, and beyond every button on this page. */
export function isPaperOriginal(instrument: InstrumentFact): boolean {
    return instrument.documentId === null && !!instrument.legacySignedAt;
}

/**
 * Its text no longer follows the contract.
 *
 * `PENDING_REVIEW` is deliberately NOT frozen: `agreement_render` re-renders it from current merge
 * data on every read, so a value changed underneath it is picked up rather than contradicted.
 */
export function isFrozenText(instrument: InstrumentFact): boolean {
    return (
        instrument.status === 'IN_SIGNING' ||
        instrument.status === 'COMPLETED' ||
        isPaperOriginal(instrument)
    );
}

function namesThe(instrument: InstrumentFact, types: readonly HazardDocType[]): boolean {
    return types.includes(instrument.docType);
}

/** An instrument with nothing on it — no document, no paper signature — answers for nothing. */
function isPresent(instrument: InstrumentFact): boolean {
    return isVoidable(instrument) || isPaperOriginal(instrument);
}

/**
 * How far the signing had got, as a sentence says it. EXPORTED FOR THE BOARD'S OWN VOID CEREMONY,
 * which names the same dependents this file computes — a second copy of the wording there would be
 * two screens describing one document two ways.
 */
export function signatureText(instrument: InstrumentFact): string {
    if (isPaperOriginal(instrument)) return 'signed on paper';
    if (instrument.partyCount === 0) return 'not yet out for signature';
    return `signed by ${instrument.signedCount} of ${instrument.partyCount} parties`;
}

function list(items: string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Every noun in this file is written for mid-sentence use; this is how one starts one. */
function sentenceCase(text: string): string {
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

// ─── The citation cascade, which both doors read ─────────────────────────────

/** What a hire purchase reissue takes with it, split by what this app can actually withdraw. */
export interface CitationCascade {
    /** Live documents whose frozen text cites the master. Voided and reissued with it. */
    voidable: InstrumentFact[];
    /** Paper originals citing the master. Nothing here can withdraw them; they are reported. */
    unvoidable: InstrumentFact[];
}

/**
 * The documents a hire purchase reissue leaves quoting a reference nobody can produce [D-B44].
 *
 * THE SELECTOR IS `isFrozenText`, NOT `status === 'COMPLETED'`, and the difference is a real
 * document. An `IN_SIGNING` dependent's text is EQUALLY frozen and cites the EQUALLY dead
 * reference — `agreement_render` returns the frozen markdown verbatim once `content_sha256` is
 * written, and issuing the signing link is what wrote it. A `PENDING_REVIEW` dependent re-renders
 * live from current merge data on every read, picks the new reference up by itself, and must NOT
 * be voided: withdrawing a document that was about to heal is a signature ceremony nobody owed.
 *
 * A PAPER ORIGINAL CITES IT TOO AND IS NOBODY'S TO TAKE BACK, so it goes to `unvoidable` — the
 * same bucket the vehicle and money rules already use for the fact that a signed copy off-system
 * will go on saying what it says.
 *
 * PURE, AND ORDER-PRESERVING. `buildBoardSlots` pushes HIRE_PURCHASE, WITNESS, then each
 * INSURANCE_LOAN, then each LOAN, then each DEPOSIT, and `instrumentsFromSlots` is a `.map` — so a
 * caller that concatenates its master ahead of this list is already reissuing in board order, which
 * is the order the driver's pending list then offers them.
 */
export function citationCascade({
    instruments,
}: {
    instruments: InstrumentFact[];
}): CitationCascade {
    const citing = instruments.filter(
        (instrument) => namesThe(instrument, CITING_INSTRUMENTS) && isFrozenText(instrument),
    );
    return { voidable: citing.filter(isVoidable), unvoidable: citing.filter(isPaperOriginal) };
}

// ─── 1 · Changing the vehicle ────────────────────────────────────────────────

export type VehicleChangeMode = 'refused' | 'hazard' | 'open';

/** The three preparation items that stay green while describing the previous car. */
export interface VehiclePrepFlags {
    service: boolean;
    roadworthiness: boolean;
    insurance: boolean;
}

export interface VehicleChangeControl {
    mode: VehicleChangeMode;
    /** The standing sentence the assignment block prints. Always whole. */
    reason: string;
    /** The destructive line above the diff, when a confirm is needed. */
    consequence: string | null;
    /** Live documents the change will void and reissue, in order. */
    voidable: InstrumentFact[];
    /**
     * Live instruments this screen cannot withdraw. Non-empty is the standing banner: a paper
     * original names the car it was signed over and no button here can take it back.
     */
    unvoidable: InstrumentFact[];
    /**
     * The standing banner, whenever there is an unvoidable instrument — in EVERY mode, including
     * `refused`. It is a fact about somebody else's paper rather than a consequence of the flow, so
     * it does not stop being true because the flow is closed.
     */
    banner: string | null;
    /** Gates that stay green while describing the previous car — the collateral list. */
    collateral: string[];
}

const COLLATERAL: { item: keyof VehiclePrepFlags; line: string }[] = [
    {
        item: 'service',
        line: 'Service and conversion — the work on file was done on the previous car, and the gate stays green',
    },
    {
        item: 'roadworthiness',
        line: 'Roadworthiness — the certificate on file names the previous plate, and the gate stays green',
    },
    {
        item: 'insurance',
        line: 'Insurance — the junction is contract-scoped while the policy is vehicle-scoped, so cover for the previous car keeps clearing the gate',
    },
];

/**
 * Whether the vehicle on this contract can be changed, and at what cost.
 *
 * THE COLLATERAL LIST IS COMPUTED, NOT RECITED. Three gates survive a swap while describing the
 * wrong car (`tasks/setup-redesign/ORGANISATION.md`, correction d) — but only the ones that are
 * currently GREEN are staying green, and naming a roadworthiness certificate that does not exist
 * teaches an admin to skim the list that will one day matter.
 */
export function vehicleChangeControl({
    contractId,
    status,
    instruments,
    prepComplete,
    hasVehicle,
}: {
    contractId: number;
    /** The latest `contract_updates` status. */
    status: string;
    instruments: InstrumentFact[];
    prepComplete: VehiclePrepFlags;
    /** False before anything is assigned — there is no old car to answer for. */
    hasVehicle: boolean;
}): VehicleChangeControl {
    const naming = instruments.filter(
        (instrument) => namesThe(instrument, VEHICLE_INSTRUMENTS) && isPresent(instrument),
    );
    const voidable = naming.filter(isVoidable);
    const unvoidable = naming.filter(isPaperOriginal);
    const collateral = hasVehicle
        ? COLLATERAL.filter((entry) => prepComplete[entry.item]).map((entry) => entry.line)
        : [];

    const banner =
        unvoidable.length === 0
            ? null
            : `${sentenceCase(list(unvoidable.map((row) => `the ${instrumentNoun(row)}`)))} ` +
              `${unvoidable.length === 1 ? 'was' : 'were'} signed on paper and cannot be withdrawn from ` +
              'this screen. It names the car it was signed over — if that is no longer the car on this ' +
              'contract, the original has to be replaced on paper too.';

    /**
     * PAST DRAFT, REFUSED — and the sentence says whose refusal it is. The database raises on a
     * change of driver or witness once a non-DRAFT contract still carries live documents; the one
     * trigger that watches `vehicle_id` reaches the Vehicle Schedule alone and is silent outside
     * DRAFT, so past DRAFT this refusal is the screen's own and claiming otherwise would send an
     * admin looking for a SQL error that will never arrive.
     *
     * AND IT IS NOT THE MONEY'S POSTURE [D-B42, D-B43]. `agreement_void` withdraws any document at
     * any status with a reason, so the Financials canvas corrects a figure past DRAFT and reissues
     * at it. The reason the vehicle does not follow is a difference in kind rather than in risk,
     * and the sentence has to say so or the two rules read as an inconsistency.
     */
    if (status !== 'DRAFT') {
        return {
            mode: 'refused',
            reason:
                `Contract #${contractId} is ${status}. The car it activated on is the car it names, so the ` +
                'vehicle is fixed from here — the same posture the database takes for a change of driver ' +
                'or witness. That guard watches `driver_id` and `witness_id` only; this refusal is the ' +
                'screen’s own. The money is not held the same way: a wrong price is a term corrected on ' +
                'this contract, and the hire purchase agreement is voided and reissued at the right ' +
                'figure. A different car is a different agreement, not a corrected one.',
            consequence: null,
            voidable: [],
            unvoidable,
            banner,
            collateral: [],
        };
    }

    if (voidable.length === 0 && unvoidable.length === 0) {
        return {
            mode: 'open',
            reason: hasVehicle
                ? 'No instrument names this car yet, so a change costs nothing but the preparation recorded ' +
                  'against it. Picking another one clears the odometer.'
                : 'The hire purchase agreement names the car it is written over, so it cannot be rendered ' +
                  'until this contract has one.',
            consequence: null,
            voidable: [],
            unvoidable: [],
            banner: null,
            collateral,
        };
    }

    const nouns = list(naming.map((instrument) => `the ${instrumentNoun(instrument)}`));
    const states = list(naming.map(signatureText));

    if (voidable.length === 0) {
        // Paper only: there is a signed original naming the old car and nothing here can withdraw
        // it. The reason speaks about the change; the BANNER carries the paper fact, once.
        return {
            mode: 'hazard',
            reason:
                'Changing the vehicle repoints this contract and clears the odometer. It cannot undo a ' +
                'signature already taken on paper, which is why it asks first.',
            consequence:
                `${sentenceCase(nouns)} is a paper original. This change cannot void it — the signed copy ` +
                'will go on naming the previous car until it is replaced on paper.',
            voidable: [],
            unvoidable,
            banner,
            collateral,
        };
    }

    return {
        mode: 'hazard',
        reason:
            `Changing the vehicle voids ${nouns} — ${states} — and reissues it from the new car. It also ` +
            'clears the odometer, and leaves the preparation already recorded describing the previous one.',
        consequence:
            `This voids ${nouns}, ${states}. Every signature already taken stops counting and all parties ` +
            'must sign again.',
        voidable,
        unvoidable,
        banner,
        collateral,
    };
}

// ─── 2 · Changing the overdue interest rate ──────────────────────────────────

export interface OverdueRateControl {
    editable: boolean;
    /** Why it is not editable, printed under the clause. Null while it is. */
    reason: string | null;
    /**
     * The live document the confirm routes through, when there is one.
     *
     * ONLY THE HIRE PURCHASE AGREEMENT. The rate is stated on its schedule; a `PENDING_REVIEW` loan document
     * re-renders live and needs nothing. The confirm exists because the master instrument is the one
     * an admin has already REVIEWED and may be about to issue a link against — and
     * `agreement_freeze_if_needed` refuses a link whose hash was taken before the change
     * (`AGREEMENT_CONTENT_CHANGED`). Voiding and reissuing gives it a fresh reference and forces a
     * fresh read instead of a refusal nobody can explain.
     */
    confirmVia: InstrumentFact | null;
}

/**
 * Whether the overdue rate can be typed into.
 *
 * `contracts.overdue_interest_rate` is NOT NULL DEFAULT 365, so this is never a fill — it is a
 * check with an edit affordance, and the affordance closes the moment somebody's copy stops
 * tracking the column.
 */
export function overdueRateControl({
    contractId,
    status,
    instruments,
}: {
    contractId: number;
    status: string;
    instruments: InstrumentFact[];
}): OverdueRateControl {
    if (status !== 'DRAFT') {
        return {
            editable: false,
            reason:
                `Contract #${contractId} is ${status}. The rate printed on its executed agreement is the ` +
                'rate the overdue engine charges, and moving one without the other is how a driver is ' +
                'billed at a figure their contract does not carry.',
            confirmVia: null,
        };
    }

    const frozen = instruments.find(
        (instrument) => printsTheOverdueRate(instrument) && isFrozenText(instrument),
    );
    if (frozen) {
        const noun = instrumentNoun(frozen);
        return {
            editable: false,
            reason: isPaperOriginal(frozen)
                ? `The ${noun} was signed on paper. Its text quotes the rate this contract was written at, ` +
                  'and nothing here can re-render it — so the rate is read-only while that original stands.'
                : `The ${noun} is ${signatureText(frozen)} and its text is frozen at the rate it was ` +
                  'rendered with. Changing the column now would leave that document quoting one figure ' +
                  'while the overdue engine charges another. Void it first, and the rate reopens.',
            confirmVia: null,
        };
    }

    const master =
        instruments.find(
            (instrument) => instrument.docType === 'HIRE_PURCHASE' && isVoidable(instrument),
        ) ?? null;

    return { editable: true, reason: null, confirmVia: master };
}

// ─── 3 · Changing the money — the schedule's price and instalment ────────────

export interface MoneyChangeControl {
    /**
     * A frozen instrument prints one of these figures, so the commit goes through the confirm.
     * False is the ordinary case and means the block's own bar writes straight through.
     */
    hazard: boolean;
    /** The destructive line above the clause diff. Null while there is no hazard. */
    consequence: string | null;
    /** The commit bar's line, so it says what pressing Save is about to open. Null when it is not. */
    hint: string | null;
    /**
     * Live documents the change will void and reissue, in BOARD ORDER — the master first, then
     * whatever cites it. The order is the driver's pending list's, so signing top-down never hits
     * `AGREEMENT_PRIOR_SIGNATURE_REQUIRED`.
     */
    voidable: InstrumentFact[];
    /** Frozen instruments this screen cannot withdraw — a paper original, always. */
    unvoidable: InstrumentFact[];
}

/**
 * How many documents the correction destroys, as a sentence starts the count. Two is the smallest
 * a cascade can be (a master and one dependent) and today's board tops out at four — the hire
 * purchase agreement, the witness agreement and two insurance advances — so the digit below is a
 * fallback for a board nobody has minted rather than the ordinary answer.
 */
const COUNT_WORDS: Record<number, string> = { 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five' };

/** One document with its own party count attached, so three states cannot float free of their nouns. */
function withState(instrument: InstrumentFact): string {
    return `the ${instrumentNoun(instrument)} (${signatureText(instrument)})`;
}

/**
 * The consequence line when the correction takes dependents with it.
 *
 * EACH STATE IS ATTACHED TO ITS OWN NOUN, which is why this is not the single-document sentence
 * with a longer list in it: `list(frozen.map(signatureText))` would read "signed by 2 of 2
 * parties, signed by 2 of 2 parties and signed by 1 of 2 parties" — three states and no owners.
 */
function cascadeConsequence(
    master: InstrumentFact[],
    dependents: InstrumentFact[],
    total: number,
): string {
    const masterNouns = list(
        master.map((instrument) => `the ${instrumentNoun(instrument)}`),
    );
    return (
        `This voids ${list(master.map(withState))} and reissues it at the new figures. ` +
        `${sentenceCase(list(dependents.map(withState)))} ` +
        `${dependents.length === 1 ? 'goes' : 'go'} with it: ` +
        `${dependents.length === 1 ? 'it cites' : 'each cites'} ${masterNouns} by its reference ` +
        `number, and a reissue mints a new one. ${COUNT_WORDS[total] ?? total} documents are ` +
        'reissued, every signature already taken on them stops counting, and all parties must ' +
        'sign again.'
    );
}

/**
 * Whether the price and the instalment can be saved without asking, and what asking has to say.
 *
 * THE TRIGGER IS THE FROZEN-TEXT RULE, THE SAME ONE THE OVERDUE RATE USES. `agreement_render` renders a
 * `PENDING_REVIEW` document LIVE from current merge data, so a price changed underneath one is
 * picked up rather than contradicted and there is nothing to confirm; once the text is frozen
 * (`IN_SIGNING`, `COMPLETED`, or a paper original) the document quotes a figure the contract no
 * longer carries, and `agreement_freeze_if_needed` will refuse the next signing link with
 * `AGREEMENT_CONTENT_CHANGED` — a refusal nobody downstream can explain. Voiding and reissuing is
 * what turns that into a fresh document at the new figure.
 *
 * IT DOES NOT REFUSE, AND THAT IS THE DIFFERENCE FROM THE RATE. The rate is `NOT NULL DEFAULT 365`
 * — a check, not a fill, and one whose value the overdue engine charges every night whatever any
 * document says, so it goes read-only rather than let the column and the engine disagree. The
 * price and the instalment are the contract's own terms: they can be genuinely wrong, correcting
 * them is a real move, and the honest posture is to show what the correction costs and then make
 * it. `DESIGN-DIRECTION.md` §A5.3 names this case by hand — "any money change once an agreement
 * exists" — and puts it in the hazard dialog, not behind a lock.
 *
 * AND THE CORRECTION CARRIES ITS DEPENDENTS [D-B44]. Reissuing the hire purchase agreement mints a
 * new reference number, which the witness agreement and the insurance premium advance quote in
 * text of their own that is already frozen — so a correction that withdrew the master alone would
 * leave two executed documents citing an instrument nobody can produce, and a human voiding and
 * reissuing them by hand afterwards. `citationCascade` is that list, and it is folded in here
 * ONLY where the master is genuinely being withdrawn.
 */
/**
 * WHAT THE SERVER DOES TO THE VEHICLE SCHEDULE WHEN THIS CONTRACT'S MONEY OR CAR MOVES [D-B48].
 *
 * REPORTED, NOT PERFORMED — which is the whole reason this is a function of its own rather than a
 * member of `MONEY_INSTRUMENTS`. Everything in those lists is voided BY THIS CLIENT through
 * `agreement_void`, and that door deliberately KEEPS the referent (a reissue re-copies the
 * contract's cash price onto it). `trg_contracts_void_partner_schedule` does the opposite: it
 * DELETES the document and the unsealed referent together, "leaving it would let the next
 * generation reuse it silently" (`20260908010000:5262-5269`). Adding the schedule to a list this
 * screen voids by hand would withdraw it through the wrong door and leave a stale figure behind
 * for the replacement to inherit.
 *
 * IT IS AN ADVISORY, AND A GATE IN ONE PLACE ONLY. Nothing new is confirmed: the sentence stands
 * on the canvas so the cost is known before the save, because the alternative is a schedule
 * vanishing without a word — and on the ruled order (the partner signs FIRST) the schedule usually
 * exists while no hire purchase agreement does, so no money confirm is opened at all. The one
 * place it blocks is `commitMoneyChange` [D-B59]: a `refused` note stops a cash-price or
 * selling-price write before the hire purchase agreement is voided, because the server would
 * refuse that write only after the void had already happened.
 *
 * THE TRIGGER IS `AFTER UPDATE OF cash_price, management_rate, sold_at, vehicle_id` AND DRAFT-ONLY
 * (the cash price joined at [D-B59]). Past DRAFT it returns NULL before reading anything, so
 * nothing is owed and this answers null — which is also why the fee is named here and nowhere else
 * on the canvas: the management rate has no hazard control of its own because, past DRAFT,
 * changing it costs nothing this screen can see.
 *
 * `PRESENT` IS A LIVE DOCUMENT, NOT A SEAL. The trigger loops `agreement_documents` and refuses
 * only where the referent it points at is sealed, so a sealed referent whose document is already
 * gone is not a refusal — it is nothing at all.
 */
export interface PartnerScheduleMoneyNote {
    /** `refused` — the server raises; `withdrawn` — the server deletes; `null` — nothing happens. */
    kind: 'refused' | 'withdrawn';
    sentence: string;
}

export function partnerScheduleMoneyNote({
    status,
    instruments,
}: {
    status: string;
    instruments: InstrumentFact[];
}): PartnerScheduleMoneyNote | null {
    if (status !== 'DRAFT') return null;

    const schedule = instruments.find(
        (instrument) => instrument.docType === 'PARTNER_MANAGEMENT' && instrument.documentId !== null,
    );
    if (!schedule) return null;

    if (schedule.legacySignedAt !== null) {
        return {
            kind: 'refused',
            sentence:
                'This contract’s vehicle schedule is executed, and the database refuses any change ' +
                'to the cash price, the selling price, the management fee or the vehicle while it ' +
                'stands. Withdraw it with a stated reason first, then raise another on the ' +
                'corrected figures.',
        };
    }

    return {
        kind: 'withdrawn',
        sentence:
            'Changing the cash price, the selling price, the management fee or the vehicle ' +
            'withdraws the vehicle schedule; it is raised again afterwards and states the ' +
            'contract’s Cash price.',
    };
}

export function moneyChangeControl({
    instruments,
}: {
    instruments: InstrumentFact[];
}): MoneyChangeControl {
    const frozen = instruments.filter(
        (instrument) => namesThe(instrument, MONEY_INSTRUMENTS) && isFrozenText(instrument),
    );

    if (frozen.length === 0) {
        return { hazard: false, consequence: null, hint: null, voidable: [], unvoidable: [] };
    }

    const master = frozen.filter(isVoidable);
    const paper = frozen.filter(isPaperOriginal);
    const nouns = list(frozen.map((instrument) => `the ${instrumentNoun(instrument)}`));
    const states = list(frozen.map(signatureText));

    if (master.length === 0) {
        // Paper only. There is nothing here to void, and the confirm says so instead of offering
        // a flow that would quietly leave the signed original quoting the previous figure.
        //
        // AND NOTHING CASCADES FROM IT [D-B44]. A paper original is not reissued, so no new
        // reference is minted and the documents citing the old one go on citing something that
        // still exists. The cascade is a consequence of the REPLACEMENT, not of the change.
        return {
            hazard: true,
            consequence:
                `${sentenceCase(nouns)} is a paper original quoting the figures below. This change ` +
                'cannot void it — the signed copy will go on naming the old ones until it is replaced ' +
                'on paper.',
            hint: 'A paper original quotes these figures · saving asks first',
            voidable: [],
            unvoidable: paper,
        };
    }

    const cascade = citationCascade({ instruments });
    const voidable = [...master, ...cascade.voidable];
    const unvoidable = [...paper, ...cascade.unvoidable];

    return {
        hazard: true,
        consequence:
            cascade.voidable.length === 0
                ? // THE SINGLE-DOCUMENT SENTENCE IS UNCHANGED, BYTE FOR BYTE. It is the ordinary
                  // case, it was already right, and the cascade wording below would report a
                  // second document where there is only one.
                  `This voids ${nouns}, ${states}, and reissues it at the new figures. Every signature ` +
                  'already taken stops counting and all parties must sign again.'
                : cascadeConsequence(master, cascade.voidable, voidable.length),
        hint: 'A signed agreement quotes these figures · saving asks first',
        voidable,
        unvoidable,
    };
}
